import { db } from '../lib/db/schema';
import type {
  OfflineRouteSurvey,
  OfflineIncidentReport,
  OfflineCustomerFeedback,
  CachedSurvey,
  CachedSurveysPagination,
} from '../lib/db/schema';
import { createRouteSurvey } from '../api/route-survey.api';
import { createIncidentReport } from '../api/incident-report.api';
import { createCustomerFeedback } from '../api/customer-feedback.api';
import { SurveyType, type SurveyTableItem } from '../types/surveys.types';
import {
  CACHE_DURATION,
  CACHE_LIMITS,
  OFFLINE_SURVEY_STATUS,
} from '../constants/offline.constants';

/**
 * Generate a unique local ID
 */
function generateLocalId(): string {
  return `offline-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Submit route survey offline
 */
export async function submitRouteSurveyOffline(data: {
  categories: OfflineRouteSurvey['categories'];
  enpsScore: number;
  enpsComments?: string;
}): Promise<string> {
  const survey: OfflineRouteSurvey = {
    localId: generateLocalId(),
    categories: data.categories,
    enpsScore: data.enpsScore,
    enpsComments: data.enpsComments,
    status: OFFLINE_SURVEY_STATUS.PENDING,
    createdAt: Date.now(),
    retryCount: 0,
  };

  await db.offlineRouteSurveys.add(survey);
  return survey.localId;
}

/**
 * Submit customer feedback offline
 */
export async function submitCustomerFeedbackOffline(data: {
  customerName: string;
  customerFeedback: string;
}): Promise<string> {
  const feedback: OfflineCustomerFeedback = {
    localId: generateLocalId(),
    customerName: data.customerName,
    customerFeedback: data.customerFeedback,
    status: OFFLINE_SURVEY_STATUS.PENDING,
    createdAt: Date.now(),
    retryCount: 0,
  };

  await db.offlineCustomerFeedbacks.add(feedback);
  return feedback.localId;
}

/**
 * Submit incident report offline
 */
export async function submitIncidentReportOffline(
  description: string,
  images: File[],
): Promise<string> {
  const report: OfflineIncidentReport = {
    localId: generateLocalId(),
    description,
    images,
    status: OFFLINE_SURVEY_STATUS.PENDING,
    createdAt: Date.now(),
    retryCount: 0,
  };

  await db.offlineIncidentReports.add(report);
  return report.localId;
}

/**
 * Get all offline route surveys
 */
export async function getOfflineRouteSurveys() {
  return await db.offlineRouteSurveys.orderBy('createdAt').reverse().toArray();
}

/**
 * Get all offline customer feedbacks
 */
export async function getOfflineCustomerFeedbacks() {
  return await db.offlineCustomerFeedbacks
    .orderBy('createdAt')
    .reverse()
    .toArray();
}

/**
 * Get all offline incident reports
 */
export async function getOfflineIncidentReports() {
  return await db.offlineIncidentReports
    .orderBy('createdAt')
    .reverse()
    .toArray();
}

/**
 * Get pending surveys count
 */
export async function getPendingSurveysCount(): Promise<{
  routeSurveys: number;
  incidentReports: number;
  customerFeedbacks: number;
  total: number;
}> {
  const statuses = [
    OFFLINE_SURVEY_STATUS.PENDING,
    OFFLINE_SURVEY_STATUS.SYNCING,
    OFFLINE_SURVEY_STATUS.FAILED,
  ];

  const routeSurveys = await db.offlineRouteSurveys
    .where('status')
    .anyOf(statuses)
    .count();

  const incidentReports = await db.offlineIncidentReports
    .where('status')
    .anyOf(statuses)
    .count();

  const customerFeedbacks = await db.offlineCustomerFeedbacks
    .where('status')
    .anyOf(statuses)
    .count();

  return {
    routeSurveys,
    incidentReports,
    customerFeedbacks,
    total: routeSurveys + incidentReports + customerFeedbacks,
  };
}

/**
 * Sync a single route survey
 */
async function syncRouteSurvey(survey: OfflineRouteSurvey): Promise<boolean> {
  try {
    await db.offlineRouteSurveys.update(survey.id!, {
      status: OFFLINE_SURVEY_STATUS.SYNCING,
    });

    await createRouteSurvey({
      categories: survey.categories,
      enpsScore: survey.enpsScore,
      enpsComments: survey.enpsComments,
      createdAt: new Date(survey.createdAt), // Pass original timestamp
    });

    // Delete the survey from IndexedDB after successful sync
    await db.offlineRouteSurveys.delete(survey.id!);

    return true;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';

    await db.offlineRouteSurveys.update(survey.id!, {
      status:
        survey.retryCount >= 2
          ? OFFLINE_SURVEY_STATUS.FAILED
          : OFFLINE_SURVEY_STATUS.PENDING,
      retryCount: survey.retryCount + 1,
      errorMessage,
    });

    return false;
  }
}

/**
 * Sync a single incident report
 */
async function syncIncidentReport(
  report: OfflineIncidentReport,
): Promise<boolean> {
  try {
    await db.offlineIncidentReports.update(report.id!, {
      status: OFFLINE_SURVEY_STATUS.SYNCING,
    });

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('description', report.description);
    formData.append('createdAt', new Date(report.createdAt).toISOString()); // Pass original timestamp
    report.images.forEach((image) => {
      formData.append('photos', image);
    });

    await createIncidentReport(formData);

    // Delete the report from IndexedDB after successful sync
    await db.offlineIncidentReports.delete(report.id!);

    return true;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';

    await db.offlineIncidentReports.update(report.id!, {
      status:
        report.retryCount >= 2
          ? OFFLINE_SURVEY_STATUS.FAILED
          : OFFLINE_SURVEY_STATUS.PENDING,
      retryCount: report.retryCount + 1,
      errorMessage,
    });

    return false;
  }
}

/**
 * Sync a single customer feedback
 */
async function syncCustomerFeedback(
  feedback: OfflineCustomerFeedback,
): Promise<boolean> {
  try {
    await db.offlineCustomerFeedbacks.update(feedback.id!, {
      status: OFFLINE_SURVEY_STATUS.SYNCING,
    });

    await createCustomerFeedback({
      customerName: feedback.customerName,
      customerFeedback: feedback.customerFeedback,
      createdAt: new Date(feedback.createdAt), // Pass original timestamp
    });

    await db.offlineCustomerFeedbacks.delete(feedback.id!);

    return true;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';

    await db.offlineCustomerFeedbacks.update(feedback.id!, {
      status:
        feedback.retryCount >= 2
          ? OFFLINE_SURVEY_STATUS.FAILED
          : OFFLINE_SURVEY_STATUS.PENDING,
      retryCount: feedback.retryCount + 1,
      errorMessage,
    });

    return false;
  }
}

/**
 * Sync all pending surveys
 */
export async function syncPendingSurveys(): Promise<{
  synced: number;
  failed: number;
}> {
  let synced = 0;
  let failed = 0;

  // Sync route surveys
  const pendingRouteSurveys = await db.offlineRouteSurveys
    .where('status')
    .anyOf([OFFLINE_SURVEY_STATUS.PENDING, OFFLINE_SURVEY_STATUS.FAILED])
    .toArray();

  for (const survey of pendingRouteSurveys) {
    const success = await syncRouteSurvey(survey);
    if (success) {
      synced++;
    } else {
      failed++;
    }
  }

  // Sync incident reports
  const pendingIncidentReports = await db.offlineIncidentReports
    .where('status')
    .anyOf([OFFLINE_SURVEY_STATUS.PENDING, OFFLINE_SURVEY_STATUS.FAILED])
    .toArray();

  for (const report of pendingIncidentReports) {
    const success = await syncIncidentReport(report);
    if (success) {
      synced++;
    } else {
      failed++;
    }
  }

  // Sync customer feedbacks
  const pendingCustomerFeedbacks = await db.offlineCustomerFeedbacks
    .where('status')
    .anyOf([OFFLINE_SURVEY_STATUS.PENDING, OFFLINE_SURVEY_STATUS.FAILED])
    .toArray();

  for (const feedback of pendingCustomerFeedbacks) {
    const success = await syncCustomerFeedback(feedback);
    if (success) {
      synced++;
    } else {
      failed++;
    }
  }

  return { synced, failed };
}

/**
 * Retry failed survey
 */
export async function retryFailedSurvey(
  localId: string,
  type: SurveyType,
): Promise<boolean> {
  if (type === SurveyType.ROUTE_SURVEY) {
    const survey = await db.offlineRouteSurveys
      .where('localId')
      .equals(localId)
      .first();

    if (survey && survey.status === OFFLINE_SURVEY_STATUS.FAILED) {
      await db.offlineRouteSurveys.update(survey.id!, {
        status: OFFLINE_SURVEY_STATUS.PENDING,
        retryCount: 0,
      });
      return await syncRouteSurvey(survey);
    }
  } else if (type === SurveyType.INCIDENT_REPORT) {
    const report = await db.offlineIncidentReports
      .where('localId')
      .equals(localId)
      .first();

    if (report && report.status === OFFLINE_SURVEY_STATUS.FAILED) {
      await db.offlineIncidentReports.update(report.id!, {
        status: OFFLINE_SURVEY_STATUS.PENDING,
        retryCount: 0,
      });
      return await syncIncidentReport(report);
    }
  } else if (type === SurveyType.CUSTOMER_FEEDBACK) {
    const feedback = await db.offlineCustomerFeedbacks
      .where('localId')
      .equals(localId)
      .first();

    if (feedback && feedback.status === OFFLINE_SURVEY_STATUS.FAILED) {
      await db.offlineCustomerFeedbacks.update(feedback.id!, {
        status: OFFLINE_SURVEY_STATUS.PENDING,
        retryCount: 0,
      });
      return await syncCustomerFeedback(feedback);
    }
  }

  return false;
}

/**
 * Cache survey configuration
 * Config never expires - it's updated on every fetch when online
 */
export async function cacheSurveyConfig(config: any): Promise<void> {
  const cached = {
    id: 1, // Single config
    data: config,
    cachedAt: Date.now(),
    expiresAt: Date.now() + CACHE_DURATION.SURVEY_CONFIG,
  };

  await db.cachedSurveyConfig.put(cached);
}

/**
 * Get cached survey configuration
 * Returns cached config without expiration check (never expires)
 */
export async function getCachedSurveyConfig() {
  const cached = await db.cachedSurveyConfig.get(1);

  if (!cached) {
    return null;
  }

  // Survey config never expires - always return if available
  return cached.data;
}

/**
 * Cache surveys data for offline viewing
 * Updates cache every time data is fetched when online
 */
export async function cacheSurveys(
  surveys: SurveyTableItem[],
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  },
): Promise<void> {
  const now = Date.now();

  // Clear existing cached surveys
  await db.cachedSurveys.clear();

  // Cache the surveys (limit defined in constants)
  const surveysToCache = surveys.slice(0, CACHE_LIMITS.SURVEY_TABLE_MAX_ITEMS);
  const cachedSurveys: CachedSurvey[] = surveysToCache.map((survey) => ({
    uuid: survey.uuid,
    accountName: survey.accountName,
    accountUuid: survey.accountUuid,
    type: survey.type as SurveyType,
    date: survey.date,
    driverName: survey.driverName,
    driverUuid: survey.driverUuid,
    managerName: survey.managerName,
    alertType: survey.alertType,
    overallScore: survey.overallScore,
    enpsScore: survey.enpsScore,
    commentsCount: survey.commentsCount,
    viewed: survey.viewed,
    categoryScore: survey.categoryScore,
    comments: survey.comments,
    cachedAt: now,
  }));

  await db.cachedSurveys.bulkAdd(cachedSurveys);

  // Cache pagination metadata
  const paginationData: CachedSurveysPagination = {
    id: 1, // Single record
    page: pagination.page,
    pageSize: pagination.pageSize,
    total: pagination.total,
    pageCount: pagination.pageCount,
    cachedAt: now,
    expiresAt: now + CACHE_DURATION.SURVEY_TABLE,
  };

  await db.cachedSurveysPagination.put(paginationData);
}

/**
 * Get cached surveys for offline viewing
 */
export async function getCachedSurveys(): Promise<{
  surveys: SurveyTableItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
} | null> {
  const paginationData = await db.cachedSurveysPagination.get(1);

  if (!paginationData) {
    return null;
  }

  // Check if expired
  if (paginationData.expiresAt < Date.now()) {
    await db.cachedSurveys.clear();
    await db.cachedSurveysPagination.delete(1);
    return null;
  }

  const cachedSurveys = await db.cachedSurveys.orderBy('cachedAt').toArray();

  const surveys: SurveyTableItem[] = cachedSurveys.map((cached) => ({
    uuid: cached.uuid,
    accountName: cached.accountName,
    accountUuid: cached.accountUuid,
    type: cached.type as SurveyType,
    date: cached.date,
    driverName: cached.driverName,
    driverUuid: cached.driverUuid,
    managerName: cached.managerName,
    alertType: cached.alertType,
    overallScore: cached.overallScore,
    enpsScore: cached.enpsScore,
    commentsCount: cached.commentsCount,
    viewed: cached.viewed,
    // Offline cache is driver-only (see cacheSurveys call site); drivers
    // never get bulk-review eligibility, so this is always false here.
    canMarkAsViewed: false,
    categoryScore: cached.categoryScore,
    comments: cached.comments,
  }));

  return {
    surveys,
    pagination: {
      page: paginationData.page,
      pageSize: paginationData.pageSize,
      total: paginationData.total,
      pageCount: paginationData.pageCount,
    },
  };
}
